import InputField from "components/FormFields/InputField";
import { PayoutModalPropsType } from "components/PayoutManagement/types";
import { addPayoutMethod } from "constants/apiUrls";
import { PayoutMethodEnum } from "enum/common";
import { useFormik } from "formik";
import { useAppSelector } from "hooks/useRedux";
import { http } from "middleware/apiMiddleware";
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from "postcode-validator";
import { useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import ReactSelect from "react-select";
import { RootState } from "store";
import { useLazyCityListQuery } from "store/apis/cityListApi";
import { useLazyStateListQuery } from "store/apis/stateListApi";
import * as Yup from "yup";

interface IChequeFormProps {
  data: PayoutModalPropsType;
  afterSubmit: () => void;
  setIsSubmitting: (value: boolean) => void;
}

const countryCode = "US";

const ChequeForm: React.FC<IChequeFormProps> = ({
  data,
  afterSubmit,
  setIsSubmitting,
}) => {
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .max(256, "Name can be max of 256 characters")
      .required("Required"),
    address1: Yup.string()
      .max(1024, "Street Address can be of max 1024 characters")
      .required("Required"),
    city: Yup.object({
      name: Yup.string().required("Required"),
      value: Yup.string().required("Required"),
    }).required("Required"),
    state: Yup.object({
      name: Yup.string().required("Required"),
      value: Yup.string().required("Required"),
    }).required("Required"),
    zipCode: Yup.string()
      .required("Required")
      .test("zipCode", "Invalid zipCode", (value, ctx) => {
        const countryShortCode = ctx.parent.country.value;
        if (postcodeValidatorExistsForCountry(countryShortCode)) {
          return postcodeValidator(value, countryShortCode);
        }
        return true;
      }),
  });

  const selectedBusiness = useAppSelector(
    (state: RootState) => state.businesses.business
  );

  const [fetchStates, { data: states }] = useLazyStateListQuery();

  const [fetchCities, { data: cities }] = useLazyCityListQuery();

  const isEdit = data?.type === "edit";
  const initialValues = {
    name: data?.data?.name || "",
    address1: data?.data?.address1 || "",
    city: {
      name: data?.data?.city || selectedBusiness?.address?.city || "",
      value: data?.data?.city || selectedBusiness?.address?.city || "",
    },
    state: {
      name: data?.data?.state || selectedBusiness?.address?.state || "",
      value:
        data?.data?.stateShortCode ||
        selectedBusiness?.address?.stateShortCode ||
        "",
    },
    country: {
      name: data?.data?.country || "United States",
      value: data?.data?.countryShortcode || "US",
    },
    zipCode: data?.data?.zipCode || "",
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values: any) => {
      try {
        setIsSubmitting(true);
        const payload = {
          name: values.name,
          address1: values.address1,
          country: countryOptions[0].name,
          countryShortCode: countryOptions[0].value,
          state: values?.state?.name,
          stateShortCode: values?.state?.value,
          city: values?.city?.value,
          zipCode: values.zipCode,
        };
        await (isEdit ? http.patch : http.post)(
          addPayoutMethod(PayoutMethodEnum.CHECK, selectedBusiness?.uuid || ""),
          payload
        );
        afterSubmit();
      } catch (error) {
        toast("Unexpected error occurred while adding/updating payout method");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const { values, setFieldValue, errors, handleSubmit, touched } = formik;

  const countryOptions = [
    {
      name: "United States",
      value: "US",
    },
  ];

  const stateOptions = useMemo(() => {
    return states?.result?.length
      ? states?.result?.map(({ name, isoCode }) => ({
          name: name,
          value: isoCode,
        }))
      : [];
  }, [states]);

  const cityOptions = useMemo(() => {
    return cities?.result?.length
      ? cities?.result?.map(({ name, isoCode }) => ({
          name: name,
          value: name,
        }))
      : [];
  }, [cities]);

  useEffect(() => {
    setFieldValue("country", { label: "United State", value: "US" });
    fetchStates({ countryCode: "US" });
  }, []);

  return (
    <div>
      <form onSubmit={handleSubmit} id={PayoutMethodEnum.CHECK}>
        <InputField
          onChange={(e) => {
            setFieldValue("name", e.target.value);
          }}
          value={values?.name}
          label={"Full Name"}
          placeholder="Enter Full Name"
          id={""}
          error={touched.name && errors.name}
        />
        <div className="flex-col md:flex justify-between">
          <div className="w-full">
            <InputField
              onChange={(e) => {
                setFieldValue("address1", e.target.value);
              }}
              value={values?.address1}
              label={"Street Address"}
              placeholder="Enter Street Address"
              id={""}
              error={touched.address1 && errors.address1}
            />
          </div>
          <div className="w-full">
            <div className="mb-2">
              <div className="block text-sm font-medium text-gray-900 text-opacity-80">
                Select State
              </div>
              <ReactSelect
                getOptionLabel={(option) => option?.name || ""}
                value={values.state}
                className="mt-1"
                isDisabled={false}
                isSearchable={true}
                autoFocus={false}
                placeholder="Select State"
                isMulti={false}
                closeMenuOnSelect
                options={stateOptions}
                onChange={(selectedOption: any) => {
                  setFieldValue("state", selectedOption);
                  setFieldValue("city", null);
                  fetchCities({
                    countryCode: countryCode,
                    stateCode: selectedOption.value,
                  });
                }}
              />
              <>
                {touched.state && errors.state && (
                  <div className="pt-1 pl-1 text-sm text-red-600">
                    {/* @ts-ignore removing type error for nested object */}
                    {errors?.state?.name || ""}
                  </div>
                )}
              </>
            </div>
          </div>
        </div>
        <div className="flex-col md:flex-row flex justify-between gap-4">
          <div className="mb-2 w-full">
            <div className="block text-sm font-medium text-gray-900 text-opacity-80">
              Select City
            </div>
            <ReactSelect
              getOptionLabel={(option) => option?.name || ""}
              value={values.city}
              className="mt-1"
              isDisabled={false}
              isSearchable={true}
              autoFocus={false}
              placeholder="Select City"
              isMulti={false}
              closeMenuOnSelect
              options={cityOptions}
              onChange={(selectedOption: any) => {
                setFieldValue("city", selectedOption);
              }}
            />
            <>
              {touched.city && errors.city && (
                <div className="pt-1 pl-1 text-sm text-red-600">
                  {/* @ts-ignore removing type error for nested object */}
                  {errors?.city?.name || ""}
                </div>
              )}
            </>
          </div>
          <div className="w-full">
            <InputField
              onChange={(e) => {
                setFieldValue("zipCode", e.target.value);
              }}
              value={values?.zipCode}
              label={"Zipcode"}
              placeholder="Enter Zipcode"
              id={""}
              error={touched.zipCode && errors.zipCode}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChequeForm;
